// @ts-nocheck
import { useState } from 'react';
// @ts-nocheck
import { IconSettings, IconTrash, IconEdit, IconDots, IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import { Box, Button, Card, Code, Divider, Drawer, Grid, Group, Image, Menu, Modal, Popover, Stack, Text, Title, Tooltip, type BackdropVariant, type DrawerPosition, type DrawerVariant, type ModalPosition, type ModalSize, type ModalVariant } from '@/components/ui';

export default function OverlaysPage() {
  const [modalOpened, setModalOpened] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [popoverOpened, setPopoverOpened] = useState(false);

  // Modal variants states
  const [sizeModal, setSizeModal] = useState<{ opened: boolean; size: ModalSize }>({ opened: false, size: 'md' });
  const [positionModal, setPositionModal] = useState<{ opened: boolean; position: ModalPosition }>({ opened: false, position: 'center' });
  const [backdropModal, setBackdropModal] = useState<{ opened: boolean; variant: BackdropVariant }>({ opened: false, variant: 'default' });
  const [variantModal, setVariantModal] = useState<{ opened: boolean; variant: ModalVariant }>({ opened: false, variant: 'default' });
  const [thumbnailModal, setThumbnailModal] = useState(false);
  const [borderModal, setBorderModal] = useState(false);
  const [saveModal, setSaveModal] = useState(false);
  const [infoModal, setInfoModal] = useState(false);

  // Drawer variants states
  const [sizeDrawer, setSizeDrawer] = useState<{ opened: boolean; size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' }>({ opened: false, size: 'md' });
  const [variantDrawer, setVariantDrawer] = useState<{ opened: boolean; variant: DrawerVariant }>({ opened: false, variant: 'default' });
  const [thumbnailDrawer, setThumbnailDrawer] = useState(false);
  const [positionDrawer, setPositionDrawer] = useState<{ opened: boolean; position: DrawerPosition }>({ opened: false, position: 'left' });
  const [guardedDrawer, setGuardedDrawer] = useState(false);

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Overlay Components</Title>
        <Text c="dimmed" size="sm">
          Components that appear above other content - modals, drawers, tooltips, popovers, and menus
        </Text>
      </Stack>

      {/* Modal - Basic */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Modal - Basic</Title>
            <Text size="sm" c="dimmed">
              Display content in a dialog overlay
            </Text>
          </Stack>

          <Button onClick={() => setModalOpened(true)}>Open Modal</Button>

          <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title="Modal Title">
            <Stack gap="md">
              <Text size="sm">
                This is a modal dialog. It displays content above the main page and blocks interaction with
                the rest of the page until closed.
              </Text>
              <Button onClick={() => setModalOpened(false)}>Close Modal</Button>
            </Stack>
          </Modal>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Modal opened={opened} onClose={() => setOpened(false)} title="Modal Title">
  <Text size="sm">Modal content goes here</Text>
</Modal>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Modal - Sizes */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Modal - Sizes</Title>
            <Text size="sm" c="dimmed">
              Different modal sizes: xs, sm, md, lg, xl, full, auto
            </Text>
          </Stack>

          <Group gap="sm">
            <Button size="sm" onClick={() => setSizeModal({ opened: true, size: 'xs' })}>XS</Button>
            <Button size="sm" onClick={() => setSizeModal({ opened: true, size: 'sm' })}>SM</Button>
            <Button size="sm" onClick={() => setSizeModal({ opened: true, size: 'md' })}>MD</Button>
            <Button size="sm" onClick={() => setSizeModal({ opened: true, size: 'lg' })}>LG</Button>
            <Button size="sm" onClick={() => setSizeModal({ opened: true, size: 'xl' })}>XL</Button>
            <Button size="sm" onClick={() => setSizeModal({ opened: true, size: 'full' })}>Full</Button>
          </Group>

          <Modal
            opened={sizeModal.opened}
            onClose={() => setSizeModal({ ...sizeModal, opened: false })}
            title={`Modal Size: ${sizeModal.size.toUpperCase()}`}
            size={sizeModal.size}
          >
            <Text size="sm">This is a {sizeModal.size} sized modal.</Text>
          </Modal>

          <Code block language="tsx">
{`<Modal size="lg" opened={opened} onClose={close} title="Large Modal">
  Content
</Modal>`}
          </Code>
        </Stack>
      </Card>

      {/* Modal - Positions */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Modal - Positions</Title>
            <Text size="sm" c="dimmed">
              Position modals at different locations on screen
            </Text>
          </Stack>

          <Grid>
            <Grid.Col span={4}>
              <Button fullWidth size="sm" onClick={() => setPositionModal({ opened: true, position: 'top-left' })}>
                Top Left
              </Button>
            </Grid.Col>
            <Grid.Col span={4}>
              <Button fullWidth size="sm" onClick={() => setPositionModal({ opened: true, position: 'top' })}>
                Top
              </Button>
            </Grid.Col>
            <Grid.Col span={4}>
              <Button fullWidth size="sm" onClick={() => setPositionModal({ opened: true, position: 'top-right' })}>
                Top Right
              </Button>
            </Grid.Col>
            <Grid.Col span={4}>
              <Button fullWidth size="sm" onClick={() => setPositionModal({ opened: true, position: 'left' })}>
                Left
              </Button>
            </Grid.Col>
            <Grid.Col span={4}>
              <Button fullWidth size="sm" onClick={() => setPositionModal({ opened: true, position: 'center' })}>
                Center
              </Button>
            </Grid.Col>
            <Grid.Col span={4}>
              <Button fullWidth size="sm" onClick={() => setPositionModal({ opened: true, position: 'right' })}>
                Right
              </Button>
            </Grid.Col>
            <Grid.Col span={4}>
              <Button fullWidth size="sm" onClick={() => setPositionModal({ opened: true, position: 'bottom-left' })}>
                Bottom Left
              </Button>
            </Grid.Col>
            <Grid.Col span={4}>
              <Button fullWidth size="sm" onClick={() => setPositionModal({ opened: true, position: 'bottom' })}>
                Bottom
              </Button>
            </Grid.Col>
            <Grid.Col span={4}>
              <Button fullWidth size="sm" onClick={() => setPositionModal({ opened: true, position: 'bottom-right' })}>
                Bottom Right
              </Button>
            </Grid.Col>
          </Grid>

          <Modal
            opened={positionModal.opened}
            onClose={() => setPositionModal({ ...positionModal, opened: false })}
            title={`Position: ${positionModal.position}`}
            position={positionModal.position}
            size="xs"
          >
            <Text size="sm">Modal positioned at {positionModal.position}</Text>
          </Modal>

          <Code block language="tsx">
{`<Modal position="top-right" opened={opened} onClose={close}>
  Content
</Modal>`}
          </Code>
        </Stack>
      </Card>

      {/* Modal - Backdrop Variants */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Modal - Backdrop Variants</Title>
            <Text size="sm" c="dimmed">
              Different backdrop effects: default, blur, transparent, dark, light, none
            </Text>
          </Stack>

          <Group gap="sm">
            <Button size="sm" onClick={() => setBackdropModal({ opened: true, variant: 'default' })}>Default</Button>
            <Button size="sm" onClick={() => setBackdropModal({ opened: true, variant: 'blur' })}>Blur</Button>
            <Button size="sm" onClick={() => setBackdropModal({ opened: true, variant: 'transparent' })}>Transparent</Button>
            <Button size="sm" onClick={() => setBackdropModal({ opened: true, variant: 'dark' })}>Dark</Button>
            <Button size="sm" onClick={() => setBackdropModal({ opened: true, variant: 'light' })}>Light</Button>
            <Button size="sm" onClick={() => setBackdropModal({ opened: true, variant: 'none' })}>None</Button>
          </Group>

          <Modal
            opened={backdropModal.opened}
            onClose={() => setBackdropModal({ ...backdropModal, opened: false })}
            title={`Backdrop: ${backdropModal.variant}`}
            backdropVariant={backdropModal.variant}
          >
            <Text size="sm">Modal with {backdropModal.variant} backdrop</Text>
          </Modal>

          <Code block language="tsx">
{`<Modal backdropVariant="blur" opened={opened} onClose={close}>
  Content
</Modal>`}
          </Code>
        </Stack>
      </Card>

      {/* Modal - Visual Variants */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Modal - Visual Variants</Title>
            <Text size="sm" c="dimmed">
              Different visual styles: default, gradient, elevated, minimal, bordered, glass
            </Text>
          </Stack>

          <Group gap="sm">
            <Button size="sm" onClick={() => setVariantModal({ opened: true, variant: 'default' })}>Default</Button>
            <Button size="sm" onClick={() => setVariantModal({ opened: true, variant: 'gradient' })}>Gradient</Button>
            <Button size="sm" onClick={() => setVariantModal({ opened: true, variant: 'elevated' })}>Elevated</Button>
            <Button size="sm" onClick={() => setVariantModal({ opened: true, variant: 'minimal' })}>Minimal</Button>
            <Button size="sm" onClick={() => setVariantModal({ opened: true, variant: 'bordered' })}>Bordered</Button>
            <Button size="sm" onClick={() => setVariantModal({ opened: true, variant: 'glass' })}>Glass</Button>
          </Group>

          <Modal
            opened={variantModal.opened}
            onClose={() => setVariantModal({ ...variantModal, opened: false })}
            title={`Variant: ${variantModal.variant}`}
            variant={variantModal.variant}
            gradientVariant="primary"
          >
            <Text size="sm">Modal with {variantModal.variant} variant</Text>
          </Modal>

          <Code block language="tsx">
{`<Modal variant="glass" opened={opened} onClose={close}>
  Content
</Modal>

<Modal variant="gradient" gradientVariant="primary">
  Content
</Modal>`}
          </Code>
        </Stack>
      </Card>

      {/* Modal - Thumbnail */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Modal - With Thumbnail</Title>
            <Text size="sm" c="dimmed">
              Add images or content at the top of the modal
            </Text>
          </Stack>

          <Button onClick={() => setThumbnailModal(true)}>Open Modal with Thumbnail</Button>

          <Modal
            opened={thumbnailModal}
            onClose={() => setThumbnailModal(false)}
            title="Product Announcement"
            thumbnail={
              <Box style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text size="xl" fw={700} c="white">New Feature</Text>
              </Box>
            }
          >
            <Stack gap="md">
              <Text size="sm">
                Check out our latest feature release! This modal demonstrates how you can add custom thumbnails or images at the top.
              </Text>
              <Button onClick={() => setThumbnailModal(false)}>Got it!</Button>
            </Stack>
          </Modal>

          <Code block language="tsx">
{`<Modal
  opened={opened}
  onClose={close}
  thumbnail={
    <Box style={{ height: '200px', background: 'linear-gradient(...)' }}>
      <Text>Thumbnail Content</Text>
    </Box>
  }
>
  Content
</Modal>`}
          </Code>
        </Stack>
      </Card>

      {/* Modal - Custom Borders */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Modal - Custom Borders</Title>
            <Text size="sm" c="dimmed">
              Add borders on specific sides with custom colors
            </Text>
          </Stack>

          <Button onClick={() => setBorderModal(true)}>Open Modal with Custom Borders</Button>

          <Modal
            opened={borderModal}
            onClose={() => setBorderModal(false)}
            title="Important Notice"
            borderSides={['left', 'right']}
            borderColor="#228be6"
            borderWidth={4}
          >
            <Stack gap="md">
              <Text size="sm">
                This modal has custom borders on the left and right sides with a blue color.
              </Text>
              <Button onClick={() => setBorderModal(false)}>Close</Button>
            </Stack>
          </Modal>

          <Code block language="tsx">
{`<Modal
  opened={opened}
  onClose={close}
  borderSides={['left', 'right']}
  borderColor="#228be6"
  borderWidth={4}
>
  Content
</Modal>`}
          </Code>
        </Stack>
      </Card>

      {/* Modal - Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Modal - Use Cases</Title>
            <Text size="sm" c="dimmed">
              Real-world examples: save confirmation, quick info display
            </Text>
          </Stack>

          <Group gap="sm">
            <Button leftSection={<IconCheck size={16} />} onClick={() => setSaveModal(true)}>
              Save Changes Example
            </Button>
            <Button leftSection={<IconAlertCircle size={16} />} onClick={() => setInfoModal(true)}>
              Quick Info Example
            </Button>
          </Group>

          {/* Save Modal */}
          <Modal
            opened={saveModal}
            onClose={() => setSaveModal(false)}
            title="Save Changes?"
            size="sm"
            position="center"
            backdropVariant="blur"
            closeOnClickOutside={false}
          >
            <Stack gap="md">
              <Text size="sm">
                You have unsaved changes. Do you want to save them before leaving?
              </Text>
              <Group justify="flex-end" gap="sm">
                <Button variant="subtle" leftSection={<IconX size={16} />} onClick={() => setSaveModal(false)}>
                  Don't Save
                </Button>
                <Button leftSection={<IconCheck size={16} />} onClick={() => setSaveModal(false)}>
                  Save Changes
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* Info Modal */}
          <Modal
            opened={infoModal}
            onClose={() => setInfoModal(false)}
            title="Quick Information"
            size="xs"
            position="top-right"
            variant="elevated"
            backdropVariant="transparent"
          >
            <Stack gap="md">
              <Text size="sm">
                This is a quick info modal positioned at the top-right with transparent backdrop.
              </Text>
              <Button size="sm" onClick={() => setInfoModal(false)}>Got it</Button>
            </Stack>
          </Modal>

          <Code block language="tsx">
{`// Save confirmation modal
<Modal
  size="sm"
  backdropVariant="blur"
  closeOnClickOutside={false}
  title="Save Changes?"
>
  <Stack gap="md">
    <Text>You have unsaved changes...</Text>
    <Group justify="flex-end">
      <Button variant="subtle">Don't Save</Button>
      <Button>Save Changes</Button>
    </Group>
  </Stack>
</Modal>

// Quick info modal
<Modal
  size="xs"
  position="top-right"
  variant="elevated"
  backdropVariant="transparent"
  title="Quick Info"
>
  <Text>Information here...</Text>
</Modal>`}
          </Code>
        </Stack>
      </Card>

      <Divider my="xl" />

      {/* Drawer - Basic */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Drawer - Basic</Title>
            <Text size="sm" c="dimmed">
              Slide-in panel from the side of the screen
            </Text>
          </Stack>

          <Button onClick={() => setDrawerOpened(true)}>Open Drawer</Button>

          <Drawer opened={drawerOpened} onClose={() => setDrawerOpened(false)} title="Drawer Title">
            <Stack gap="md">
              <Text size="sm">
                This is a drawer that slides in from the side. Useful for navigation menus or forms.
              </Text>
              <Button onClick={() => setDrawerOpened(false)}>Close Drawer</Button>
            </Stack>
          </Drawer>

          <Code block language="tsx">
{`<Drawer opened={opened} onClose={() => setOpened(false)} title="Drawer">
  <Text>Drawer content goes here</Text>
</Drawer>`}
          </Code>
        </Stack>
      </Card>

      {/* Drawer - Sizes */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Drawer - Sizes</Title>
            <Text size="sm" c="dimmed">
              Different drawer sizes: xs, sm, md, lg, xl, full
            </Text>
          </Stack>

          <Group gap="sm">
            <Button size="sm" onClick={() => setSizeDrawer({ opened: true, size: 'xs' })}>XS</Button>
            <Button size="sm" onClick={() => setSizeDrawer({ opened: true, size: 'sm' })}>SM</Button>
            <Button size="sm" onClick={() => setSizeDrawer({ opened: true, size: 'md' })}>MD</Button>
            <Button size="sm" onClick={() => setSizeDrawer({ opened: true, size: 'lg' })}>LG</Button>
            <Button size="sm" onClick={() => setSizeDrawer({ opened: true, size: 'xl' })}>XL</Button>
            <Button size="sm" onClick={() => setSizeDrawer({ opened: true, size: 'full' })}>Full</Button>
          </Group>

          <Drawer
            opened={sizeDrawer.opened}
            onClose={() => setSizeDrawer({ ...sizeDrawer, opened: false })}
            title={`Drawer Size: ${sizeDrawer.size.toUpperCase()}`}
            size={sizeDrawer.size}
          >
            <Text size="sm">This is a {sizeDrawer.size} sized drawer.</Text>
          </Drawer>

          <Code block language="tsx">
{`<Drawer size="lg" opened={opened} onClose={close} title="Large Drawer">
  Content
</Drawer>`}
          </Code>
        </Stack>
      </Card>

      {/* Drawer - Positions */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Drawer - Positions</Title>
            <Text size="sm" c="dimmed">
              Slide in from any side: left, right, top, or bottom
            </Text>
          </Stack>

          <Group gap="sm">
            <Button size="sm" onClick={() => setPositionDrawer({ opened: true, position: 'left' })}>Left</Button>
            <Button size="sm" onClick={() => setPositionDrawer({ opened: true, position: 'right' })}>Right</Button>
            <Button size="sm" onClick={() => setPositionDrawer({ opened: true, position: 'top' })}>Top</Button>
            <Button size="sm" onClick={() => setPositionDrawer({ opened: true, position: 'bottom' })}>Bottom</Button>
          </Group>

          <Drawer
            opened={positionDrawer.opened}
            onClose={() => setPositionDrawer({ ...positionDrawer, opened: false })}
            title={`Position: ${positionDrawer.position}`}
            position={positionDrawer.position}
            size="sm"
          >
            <Text size="sm">Drawer opens from the {positionDrawer.position}.</Text>
          </Drawer>

          <Code block language="tsx">
{`<Drawer position="right" opened={opened} onClose={close}>
  Content
</Drawer>`}
          </Code>
        </Stack>
      </Card>

      {/* Drawer - Visual Variants */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Drawer - Visual Variants</Title>
            <Text size="sm" c="dimmed">
              Different visual styles: default, gradient, elevated, minimal, bordered, glass
            </Text>
          </Stack>

          <Group gap="sm">
            <Button size="sm" onClick={() => setVariantDrawer({ opened: true, variant: 'default' })}>Default</Button>
            <Button size="sm" onClick={() => setVariantDrawer({ opened: true, variant: 'gradient' })}>Gradient</Button>
            <Button size="sm" onClick={() => setVariantDrawer({ opened: true, variant: 'elevated' })}>Elevated</Button>
            <Button size="sm" onClick={() => setVariantDrawer({ opened: true, variant: 'minimal' })}>Minimal</Button>
            <Button size="sm" onClick={() => setVariantDrawer({ opened: true, variant: 'bordered' })}>Bordered</Button>
            <Button size="sm" onClick={() => setVariantDrawer({ opened: true, variant: 'glass' })}>Glass</Button>
          </Group>

          <Drawer
            opened={variantDrawer.opened}
            onClose={() => setVariantDrawer({ ...variantDrawer, opened: false })}
            title={`Variant: ${variantDrawer.variant}`}
            variant={variantDrawer.variant}
            gradientVariant="primary"
          >
            <Text size="sm">Drawer with {variantDrawer.variant} variant</Text>
          </Drawer>

          <Code block language="tsx">
{`<Drawer variant="glass" opened={opened} onClose={close}>
  Content
</Drawer>

<Drawer variant="gradient" gradientVariant="primary">
  Content
</Drawer>`}
          </Code>
        </Stack>
      </Card>

      {/* Drawer - Action Required */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Drawer - Action Required</Title>
            <Text size="sm" c="dimmed">
              Disable outside/escape close when users must act on the drawer contents
            </Text>
          </Stack>

          <Button onClick={() => setGuardedDrawer(true)}>Open Guarded Drawer</Button>

          <Drawer
            opened={guardedDrawer}
            onClose={() => setGuardedDrawer(false)}
            title="Review and Confirm"
            closeOnClickOutside={false}
            closeOnEscape={false}
            position="right"
            size="md"
            variant="elevated"
          >
            <Stack gap="md">
              <Text size="sm">
                Outside clicks and Escape are disabled. Use the actions below to close this drawer.
              </Text>
              <Group justify="flex-end" gap="sm">
                <Button variant="subtle" onClick={() => setGuardedDrawer(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setGuardedDrawer(false)}>Confirm</Button>
              </Group>
            </Stack>
          </Drawer>

          <Code block language="tsx">
{`<Drawer
  opened={opened}
  onClose={close}
  closeOnClickOutside={false}
  closeOnEscape={false}
>
  <Text>Explicit actions required.</Text>
  <Button onClick={close}>Close</Button>
</Drawer>`}
          </Code>
        </Stack>
      </Card>

      {/* Drawer - With Thumbnail */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Drawer - With Thumbnail</Title>
            <Text size="sm" c="dimmed">
              Add images or content at the top of the drawer
            </Text>
          </Stack>

          <Button onClick={() => setThumbnailDrawer(true)}>Open Drawer with Thumbnail</Button>

          <Drawer
            opened={thumbnailDrawer}
            onClose={() => setThumbnailDrawer(false)}
            title="User Profile"
            thumbnail={
              <Box style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text size="xl" fw={700} c="white">Profile</Text>
              </Box>
            }
          >
            <Stack gap="md">
              <Text size="sm">
                This drawer demonstrates how you can add custom thumbnails or images at the top.
              </Text>
              <Button onClick={() => setThumbnailDrawer(false)}>Close</Button>
            </Stack>
          </Drawer>

          <Code block language="tsx">
{`<Drawer
  opened={opened}
  onClose={close}
  thumbnail={
    <Box style={{ height: '150px', background: 'linear-gradient(...)' }}>
      <Text>Thumbnail Content</Text>
    </Box>
  }
>
  Content
</Drawer>`}
          </Code>
        </Stack>
      </Card>

      <Divider my="xl" />

      {/* Tooltip */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Tooltip</Title>
            <Text size="sm" c="dimmed">
              Show additional information on hover
            </Text>
          </Stack>

          <Stack gap="sm">
            <Tooltip label="This is a tooltip">
              <Button>Hover me</Button>
            </Tooltip>
            <Tooltip label="Tooltip with arrow" withArrow>
              <Button variant="outline">Hover for tooltip with arrow</Button>
            </Tooltip>
            <Tooltip label="Different position" position="right" withArrow>
              <Button variant="light">Right positioned tooltip</Button>
            </Tooltip>
          </Stack>

          <Code block language="tsx">
{`<Tooltip label="This is a tooltip">
  <Button>Hover me</Button>
</Tooltip>

<Tooltip label="Tooltip with arrow" withArrow>
  <Button>Hover for tooltip</Button>
</Tooltip>`}
          </Code>
        </Stack>
      </Card>

      {/* Popover */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Popover</Title>
            <Text size="sm" c="dimmed">
              Display rich content in a floating panel
            </Text>
          </Stack>

          <Popover opened={popoverOpened} onChange={setPopoverOpened} width={300} position="bottom" withArrow>
            <Popover.Target>
              <Button onClick={() => setPopoverOpened(!popoverOpened)}>Toggle Popover</Button>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Popover Title
                </Text>
                <Text size="sm">
                  This is popover content. It can contain any React components and stays open until explicitly
                  closed.
                </Text>
              </Stack>
            </Popover.Dropdown>
          </Popover>

          <Code block language="tsx">
{`<Popover width={300} position="bottom" withArrow>
  <Popover.Target>
    <Button>Toggle Popover</Button>
  </Popover.Target>
  <Popover.Dropdown>
    <Text size="sm">Popover content</Text>
  </Popover.Dropdown>
</Popover>`}
          </Code>
        </Stack>
      </Card>

      {/* Menu */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Menu</Title>
            <Text size="sm" c="dimmed">
              Display a list of actions or options
            </Text>
          </Stack>

          <Menu>
            <Menu.Target>
              <Button rightSection={<IconDots size={16} />}>Actions</Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Application</Menu.Label>
              <Menu.Item leftSection={<IconSettings size={14} />}>Settings</Menu.Item>
              <Menu.Item leftSection={<IconEdit size={14} />}>Edit</Menu.Item>
              <Menu.Divider />
              <Menu.Label>Danger zone</Menu.Label>
              <Menu.Item leftSection={<IconTrash size={14} />} color="red">
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Code block language="tsx">
{`<Menu>
  <Menu.Target>
    <Button>Actions</Button>
  </Menu.Target>

  <Menu.Dropdown>
    <Menu.Label>Application</Menu.Label>
    <Menu.Item leftSection={<IconSettings size={14} />}>
      Settings
    </Menu.Item>
    <Menu.Item leftSection={<IconEdit size={14} />}>
      Edit
    </Menu.Item>
    <Menu.Divider />
    <Menu.Label>Danger zone</Menu.Label>
    <Menu.Item leftSection={<IconTrash size={14} />} color="red">
      Delete
    </Menu.Item>
  </Menu.Dropdown>
</Menu>`}
          </Code>
        </Stack>
      </Card>
    </Stack>
  );
}
